import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import PipelineProspectCard, { PIPELINE_STATUS_COLOR } from './PipelineProspectCard';

/**
 * Module 2 — A2.7 Recruitment pipeline view (Phase 1, MANUAL — R9 correction).
 *
 * Spec (TCN_Agent_App_Modules…docx, A2.7 "UI components & screens"):
 *   - header strip with totals: prospects · by-status counts · replied rate ·
 *     forecast-this-month
 *   - kanban-lite list by status (Prospect → Onboarded) with the 9 fields
 *   - add/edit forms (ProspectFormModal, owned by the page)
 *
 * Props:
 *   - prospects: recruitment_pipeline rows (new A2.7 schema)
 *   - isDesktop: bool
 *   - onAdd: () => void — opens the add form
 *   - onEdit: (prospect) => void — opens the edit form
 */
// Funnel order for the kanban-lite sections.
const FUNNEL = ['prospect', 'contacted', 'replied', 'negotiating', 'onboarded', 'declined'];

const isThisMonth = (d) => {
  if (!d) return false;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return false;
  const now = new Date();
  return dt.getUTCFullYear() === now.getUTCFullYear() && dt.getUTCMonth() === now.getUTCMonth();
};

const AddButton = ({ onClick, t, full }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      width: full ? '100%' : undefined,
      padding: '10px 14px',
      borderRadius: 10,
      border: `1px solid ${TCN.cyan}55`,
      background: `${TCN.cyan}14`,
      color: TCN.cyan,
      fontFamily: 'inherit',
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
    }}
  >
    <Icon name="plus" size={16} color={TCN.cyan} />
    {t('tcn.m2.pipeline.add', 'Add prospect')}
  </button>
);

const StatCell = ({ label, value, color }) => (
  <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
    <div
      style={{
        fontSize: 20,
        fontWeight: 700,
        color: color ?? TCN.text,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: TCN.fontMono,
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: 10,
        color: TCN.textMute,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginTop: 2,
      }}
    >
      {label}
    </div>
  </div>
);

const PipelineView = ({ prospects = [], isDesktop = false, onAdd, onEdit }) => {
  const { t } = useTranslation();

  const stats = useMemo(() => {
    const total = prospects.length;
    const repliedCount = prospects.filter((p) => p.replied).length;
    const forecastThisMonth = prospects.filter((p) => isThisMonth(p.forecast_onboard_month)).length;
    const repliedRate = total > 0 ? Math.round((repliedCount / total) * 100) : 0;
    return { total, repliedRate, forecastThisMonth };
  }, [prospects]);

  const groups = useMemo(() => {
    const byStatus = Object.fromEntries(FUNNEL.map((s) => [s, []]));
    for (const p of prospects) {
      const k = (p.status || '').toLowerCase();
      (byStatus[k] || (byStatus[k] = [])).push(p);
    }
    return byStatus;
  }, [prospects]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header strip — totals + add (A2.7) */}
      <Card>
        <div
          style={{
            display: 'flex',
            flexDirection: isDesktop ? 'row' : 'column',
            alignItems: isDesktop ? 'center' : 'stretch',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', gap: 8, flex: 1 }}>
            <StatCell label={t('tcn.m2.pipeline.stat.prospects', 'Prospects')} value={stats.total} />
            <StatCell
              label={t('tcn.m2.pipeline.stat.repliedRate', 'Replied rate')}
              value={`${stats.repliedRate}%`}
              color={TCN.cyan}
            />
            <StatCell
              label={t('tcn.m2.pipeline.stat.forecast', 'Forecast this month')}
              value={stats.forecastThisMonth}
              color={TCN.green}
            />
          </div>
          <AddButton onClick={onAdd} t={t} full={!isDesktop} />
        </div>
      </Card>

      {prospects.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Icon name="people" size={32} color={TCN.textMute} />
            <div style={{ marginTop: 10, fontSize: 14, color: TCN.text, fontWeight: 600 }}>
              {t('tcn.m2.pipeline.empty.title', 'No prospects in your pipeline yet')}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: TCN.textDim }}>
              {t(
                'tcn.m2.pipeline.empty.body',
                'Add a prospect to start tracking your recruitment pipeline.'
              )}
            </div>
          </div>
        </Card>
      ) : (
        /* Kanban-lite list by status (Prospect → Onboarded → Declined) */
        FUNNEL.filter((s) => groups[s] && groups[s].length > 0).map((s) => {
          const color = PIPELINE_STATUS_COLOR[s] ?? TCN.textDim;
          return (
            <div key={s}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: TCN.text,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {t(`tcn.m2.pipeline.status.${s}`, s.charAt(0).toUpperCase() + s.slice(1))}
                </span>
                <span style={{ fontSize: 12, color: TCN.textDim }}>· {groups[s].length}</span>
              </div>
              <div
                style={{
                  display: isDesktop ? 'grid' : 'flex',
                  flexDirection: isDesktop ? undefined : 'column',
                  gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : undefined,
                  gap: 8,
                }}
              >
                {groups[s].map((p) => (
                  <PipelineProspectCard key={p.id} prospect={p} onClick={onEdit} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default PipelineView;
